import ConfigProvider from '@core/services/ConfigProvider';
import deepMerge from '@core/utils/deepMerge';

class ClientConfigService {
  private readonly configModules = import.meta.glob(
    '../../externalDevportal/clients/*/config/*.ts',
  );

  private readonly componentConfigModules = import.meta.glob(
    '../../externalDevportal/clients/*/config/components/*.ts',
  );

  private componentConfigCache: Record<string, Record<string, unknown>> = {};

  /**
   * Loads a globbed module and returns the resolved config object (default export or namespace).
   */
  private async loadResolvedConfig(
    loader: () => Promise<unknown>,
  ): Promise<Record<string, unknown>> {
    try {
      const clientConfigModule = await loader();
      if (clientConfigModule && typeof clientConfigModule === 'object') {
        const mod = clientConfigModule as {default?: unknown};
        const resolved = mod.default ?? clientConfigModule;
        if (
          resolved &&
          typeof resolved === 'object' &&
          !Array.isArray(resolved)
        ) {
          return resolved as Record<string, unknown>;
        }
      }
      return {};
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(
          '[ClientConfigService] Failed to load config module',
          error,
        );
      }
      return {};
    }
  }

  async getClientConfig(configType: string): Promise<Record<string, unknown>> {
    const client = ConfigProvider.value('client');

    if (!client) {
      return {};
    }

    const path = `../../externalDevportal/clients/${client}/config/${configType.toLowerCase()}.ts`;
    const loader = this.configModules[path];

    if (!loader) {
      return {};
    }

    return this.loadResolvedConfig(loader);
  }

  async getComponentConfig(
    componentName: string,
  ): Promise<Record<string, unknown>> {
    const client = ConfigProvider.value('client');

    if (!client) {
      return {};
    }

    const cacheKey = `${client}:${componentName}`;
    if (this.componentConfigCache[cacheKey]) {
      return this.componentConfigCache[cacheKey];
    }

    const path = `../../externalDevportal/clients/${client}/config/components/${componentName.toLowerCase()}.ts`;
    const loader = this.componentConfigModules[path];

    if (!loader) {
      return {};
    }

    const config = await this.loadResolvedConfig(loader);
    this.componentConfigCache[cacheKey] = config;
    return config;
  }

  async mergeWithClientConfig(
    configType: string,
    configName: string,
    defaultConfig: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    if (!defaultConfig) {
      throw new Error(`Default configuration ${configName} is not defined.`);
    }

    const clientConfig = await this.getClientConfig(configType);
    const clientConfigValue = clientConfig[configName] ?? {};

    return deepMerge(defaultConfig, clientConfigValue) as Record<
      string,
      unknown
    >;
  }
}

export default new ClientConfigService();
